/**
 * Projectiles — the thing that was missing.
 *
 * `WielderCtx.onFire` was optional and never supplied, and `fireOrSwing`
 * returned early for any `spawn` hitbox before target collection ran. Ten of
 * the twelve projectile classes use a `spawn` base hitbox, so ten of twelve
 * dealt exactly zero damage, including the service pistol the player carries as
 * a permanent off-hand and the axe's throw. This file is that missing half.
 *
 * Two decisions shape it:
 *
 *   - **One path for both sides.** Player weapons and enemy `AttackDef`s put
 *     rounds in the same pool. A bullet is a bullet; having two implementations
 *     would guarantee they drift, and the reflecting parry of docs/04 §2 needs
 *     to be able to turn one into the other by flipping two numbers.
 *
 *   - **A round resolves through `resolveHit`.** A projectile that computed its
 *     own damage would be a second, quieter damage model that never learns
 *     about composition, primers, detonation or the cell the target stands in —
 *     exactly the split docs/03 §6 exists to forbid. The cost is that a
 *     projectile carries a reference to its `ResolvedWeapon`; that is a
 *     per-shot object reference into a table that is rebuilt only on equip, not
 *     an allocation.
 *
 * SoA, fixed capacity, no allocation after construction — the same contract as
 * `sim/particles.ts`, which this models its integration on.
 */

import type { Rng } from '../core/rng';
import { BodyFlag, Comp, DamageType, Team, World } from '../entity/ecs';
import type { DamageTypeId } from '../entity/ecs';
import { Category, M, VOID } from '../sim/materials';
import type { MatId } from '../sim/materials';
import type { CellWorld } from '../sim/world';
import type { Particles } from '../sim/particles';
import { roundProfileForClass, roundProfileId } from '../entity/weaponVisual';
import { newHitResult, resolveHit, applyTerrain } from './resolve';
import type { HitInput, HitResult } from './resolve';
import { defaultStats } from './wielder';
import type { FireEvent } from './wielder';
import type {
  ComboNode, CombatStats, Hand, PrimerKindId, ResolvedWeapon, TriggerMask,
} from './types';

const CAPACITY = 512;
/** Targets a single round remembers, so a pierce shot cannot re-hit one body. */
const HIT_MEMORY = 4;
/** Terminal speed, units/sec. Above this the sweep gets expensive for nothing. */
const MAX_SPEED = 4000;
/** Matches `sim/particles.ts` so debris, bodies and bullets agree on gravity. */
const GRAVITY = 1200;

export interface ProjectileSpawn {
  x: number;
  y: number;
  /** Unit direction. */
  dirX: number;
  dirY: number;
  speed: number;
  lifetime: number;
  /** Bodies this round passes through before stopping. */
  pierce: number;
  gravityScale: number;
  /** Contact radius in units. */
  radius: number;
  owner: number;
  team: number;
  damageType: DamageTypeId;
  /** Flat damage. Read only when `weapon` is null — i.e. for enemy attacks. */
  damage: number;
  /** Cells laid along the flight path. VOID for none. */
  trailMat: MatId;
  homing: number;
  /** Bounces off terrain before expiring. */
  ricochet: number;
  splitOnImpact: number;
  /**
   * `ROUND_PROFILES` index — what this round is *drawn* as. See
   * `entity/weaponVisual.ts`. A number and not a class string because the
   * renderer reads it once per stamp per round per frame, and the object
   * columns below are contractually not read per-step.
   */
  profile: number;
  /** Present for player weapons; null for enemy attacks. */
  weapon: ResolvedWeapon | null;
  node: ComboNode | null;
  stats: CombatStats | null;
  rhythm: number;
  charge: number;
  chargePrimer: PrimerKindId | 0;
  hand: Hand;
  windows: TriggerMask;
}

export interface ProjectileHooks {
  /**
   * Same contract as `WielderCtx.onHitConfirm`, for shared presentation — and
   * now the same *arguments*, in the same order.
   *
   * `owner` stands where the wielder's `state` stands, because a round is the
   * only confirm whose thrower is not the player by construction: the pool is
   * one path for both sides (see the header), so this hook is reached by
   * whatever fired. An implementation that reads the wielder off `this` and
   * ignores `owner` credits the player for an enemy's round — #138.
   *
   * `hand` was the missing column. `hand` is recorded on spawn and read on
   * contact for `resolveHit`, but was not passed on, so `game.ts` supplied a
   * literal `'main'` to a parameter it happened not to read. Selection and
   * lookup disagreeing is the #92 / #127 / #137 shape; a hook that carries five
   * of the six things it knows invites the sixth to be invented.
   */
  onHitConfirm?(owner: number, hand: Hand, target: number, result: HitResult): void;
  /** Flat-damage sink for rounds with no `ResolvedWeapon` (enemy attacks). */
  damageEntity?(idx: number, amount: number, type: number, srcIdx: number): void;
  /** Tracers and impact spray, as real matter. Optional — headless runs omit it. */
  particles?: Particles | null;
}

/** Reused for every contact; `resolveHit` must not allocate. */
const scratchHit: HitInput = {
  attacker: 0, target: 0, weapon: null as unknown as ResolvedWeapon, node: null,
  hand: 'main', hx: 0, hy: 0, dirX: 1, dirY: 0,
  stats: defaultStats(), rhythm: 0, charge: 1, backstab: false,
  // Projectiles never grant or respect iframes: nine scattergun pellets landing
  // as one hit would make the class's identity — a cone — invisible. Each round
  // has its own hit memory instead, which is the correct granularity.
  continuous: true,
  windows: 0, chargePrimer: 0, echo: false,
};
const scratchResult = newHitResult();

export class Projectiles {
  readonly x = new Float32Array(CAPACITY);
  readonly y = new Float32Array(CAPACITY);
  readonly vx = new Float32Array(CAPACITY);
  readonly vy = new Float32Array(CAPACITY);
  readonly life = new Float32Array(CAPACITY);
  readonly grav = new Float32Array(CAPACITY);
  readonly radius = new Float32Array(CAPACITY);
  readonly pierce = new Int16Array(CAPACITY);
  readonly ricochet = new Int16Array(CAPACITY);
  readonly split = new Int16Array(CAPACITY);
  readonly owner = new Int32Array(CAPACITY);
  readonly team = new Uint8Array(CAPACITY);
  readonly damage = new Float32Array(CAPACITY);
  readonly damageType = new Uint8Array(CAPACITY);
  readonly trailMat = new Uint16Array(CAPACITY);
  readonly homing = new Float32Array(CAPACITY);
  readonly charge = new Float32Array(CAPACITY);
  readonly rhythm = new Float32Array(CAPACITY);
  readonly windows = new Int32Array(CAPACITY);
  readonly chargePrimer = new Uint8Array(CAPACITY);
  /** `ROUND_PROFILES` index. Presentation only — nothing in `step` reads it. */
  readonly profile = new Uint8Array(CAPACITY);

  /** Parallel object columns. Written on spawn, read on contact, never per-step. */
  private wp: Array<ResolvedWeapon | null> = new Array(CAPACITY).fill(null);
  private node: Array<ComboNode | null> = new Array(CAPACITY).fill(null);
  private stats: Array<CombatStats | null> = new Array(CAPACITY).fill(null);
  private hand: Hand[] = new Array(CAPACITY).fill('main');

  private hitBuf = new Int32Array(CAPACITY * HIT_MEMORY);
  private hitN = new Uint8Array(CAPACITY);

  /** Dense packing: [0, count) is live. Removal swaps with the last. */
  count = 0;
  /** Rounds dropped because the pool was full, this step. */
  droppedThisStep = 0;

  spawn(s: ProjectileSpawn): boolean {
    if (this.count >= CAPACITY) {
      this.droppedThisStep++;
      return false;
    }
    const i = this.count++;
    const len = Math.hypot(s.dirX, s.dirY) || 1;
    const speed = Math.min(MAX_SPEED, s.speed);
    this.x[i] = s.x;
    this.y[i] = s.y;
    this.vx[i] = (s.dirX / len) * speed;
    this.vy[i] = (s.dirY / len) * speed;
    this.life[i] = s.lifetime;
    this.grav[i] = s.gravityScale;
    this.radius[i] = Math.max(1, s.radius);
    this.pierce[i] = s.pierce;
    this.ricochet[i] = s.ricochet;
    this.split[i] = s.splitOnImpact;
    this.owner[i] = s.owner;
    this.team[i] = s.team;
    this.damage[i] = s.damage;
    this.damageType[i] = s.damageType;
    this.trailMat[i] = s.trailMat;
    this.homing[i] = s.homing;
    this.charge[i] = s.charge;
    this.rhythm[i] = s.rhythm;
    this.windows[i] = s.windows;
    this.chargePrimer[i] = s.chargePrimer;
    this.profile[i] = s.profile;
    this.wp[i] = s.weapon;
    this.node[i] = s.node;
    this.stats[i] = s.stats;
    this.hand[i] = s.hand;
    this.hitN[i] = 0;
    return true;
  }

  /**
   * Integrate, sweep, and resolve. Sweeping rather than teleporting matters
   * here more than it does for particles: a rail round covers 2600 units in a
   * second and would otherwise pass through every enemy and wall in the room.
   */
  step(
    w: World,
    cells: CellWorld,
    dt: number,
    rng: Rng,
    hooks: ProjectileHooks = {},
  ): void {
    if (dt <= 0) return;
    this.droppedThisStep = 0;

    for (let i = 0; i < this.count; i++) {
      if (this.homing[i] > 0) this.steer(w, i, dt);

      let nvx = this.vx[i];
      let nvy = this.vy[i] + GRAVITY * this.grav[i] * dt;
      const px = this.x[i];
      const py = this.y[i];
      const nx = px + nvx * dt;
      const ny = py + nvy * dt;

      const dist = Math.hypot(nx - px, ny - py);
      const steps = Math.max(1, Math.ceil(dist / 2));
      let dead = false;
      let sx = px;
      let sy = py;

      for (let s = 1; s <= steps && !dead; s++) {
        const t = s / steps;
        sx = px + (nx - px) * t;
        sy = py + (ny - py) * t;

        if (this.hitEntities(w, cells, i, sx, sy, rng, hooks)) {
          if (this.pierce[i] < 0) {
            dead = true;
            break;
          }
        }

        const cx = Math.floor(sx);
        const cy = Math.floor(sy);
        const m = cells.peek(cx, cy);

        // The trail is laid *behind* the round, after the cell ahead has been
        // tested. A seed-thrower that writes SPORE_DUST under its own nose and
        // then asks whether the cell it is in is solid detonates on its own
        // trail one step after launch — which is exactly what it did.
        if (this.trailMat[i] !== VOID && (m === VOID || M.solid[m] === 0)) {
          const tx = Math.round(sx - this.vx[i] * dt);
          const ty = Math.round(sy - this.vy[i] * dt);
          if (cells.peek(tx, ty) === VOID) cells.set(tx, ty, this.trailMat[i]);
        }

        // Only rigid structure stops a round.
        //
        // `M.solid` is true for LOOSE as well as RIGID, because entity
        // collision wants sand to hold you up. A projectile is not an entity:
        // testing `M.solid` here meant every weapon whose terrain interaction
        // emits at the muzzle — the seed-thrower's spore dust, the axe's ash —
        // detonated its round on its own emission one step after launch. Loose
        // matter is bored through instead, which is also what a bullet does.
        if (m !== VOID && M.category[m] === Category.RIGID) {
          this.x[i] = sx;
          this.y[i] = sy;
          if (this.impact(w, cells, i, cx, cy, nvx, nvy, rng, hooks)) {
            dead = true;
            break;
          }
          // Ricocheted: the sweep restarts from the new heading next step.
          nvx = this.vx[i];
          nvy = this.vy[i];
          break;
        }
        if (m !== VOID && M.solid[m] === 1) cells.damageCell(cx, cy, 2);
      }

      if (dead) {
        this.remove(i);
        i--;
        continue;
      }

      this.life[i] -= dt;
      if (this.life[i] <= 0) {
        this.expire(cells, i, rng, hooks);
        this.remove(i);
        i--;
        continue;
      }

      this.x[i] = sx;
      this.y[i] = sy;
      this.vx[i] = nvx;
      this.vy[i] = nvy;
    }
  }

  /** Nudge the heading toward the nearest hostile body. Homing is a module. */
  private steer(w: World, i: number, dt: number): void {
    const team = this.team[i];
    let best = -1;
    let bestD2 = 200 * 200;
    const px = this.x[i];
    const py = this.y[i];
    w.each(Comp.HEALTH, (j) => {
      if (w.hp[j] <= 0 || w.team[j] === team || w.team[j] === Team.NEUTRAL) return;
      const dx = w.x[j] - px;
      const dy = w.y[j] - py;
      const d2 = dx * dx + dy * dy;
      if (d2 < bestD2) {
        bestD2 = d2;
        best = j;
      }
    });
    if (best < 0) return;
    const sp = Math.hypot(this.vx[i], this.vy[i]) || 1;
    const dx = w.x[best] - px;
    const dy = w.y[best] - py;
    const dl = Math.hypot(dx, dy) || 1;
    const k = Math.min(1, this.homing[i] * dt * 6);
    const ux = this.vx[i] / sp + (dx / dl - this.vx[i] / sp) * k;
    const uy = this.vy[i] / sp + (dy / dl - this.vy[i] / sp) * k;
    const ul = Math.hypot(ux, uy) || 1;
    this.vx[i] = (ux / ul) * sp;
    this.vy[i] = (uy / ul) * sp;
  }

  /** Returns true if this round connected with a body at (sx, sy). */
  private hitEntities(
    w: World,
    cells: CellWorld,
    i: number,
    sx: number,
    sy: number,
    rng: Rng,
    hooks: ProjectileHooks,
  ): boolean {
    const team = this.team[i];
    const r = this.radius[i];
    const own = this.owner[i];
    let connected = false;

    w.each(Comp.HEALTH, (j) => {
      if (connected && this.pierce[i] < 0) return;
      if (j === own || w.hp[j] <= 0) return;
      if (w.team[j] === team || w.team[j] === Team.NEUTRAL) return;
      if (Math.abs(w.x[j] - sx) > w.halfW[j] + r) return;
      if (Math.abs(w.y[j] - sy) > w.halfH[j] + r) return;
      if (this.remembers(i, j)) return;

      this.remember(i, j);
      this.resolve(w, cells, i, j, sx, sy, rng, hooks);
      this.pierce[i]--;
      connected = true;
    });
    return connected;
  }

  private resolve(
    w: World,
    cells: CellWorld,
    i: number,
    j: number,
    sx: number,
    sy: number,
    rng: Rng,
    hooks: ProjectileHooks,
  ): void {
    const wp = this.wp[i];
    const sp = Math.hypot(this.vx[i], this.vy[i]) || 1;

    if (wp === null) {
      // Enemy attacks carry a flat number and no module chain. They still route
      // through the one damage door so death handling stays in one place.
      const amount = this.damage[i];
      if (hooks.damageEntity) hooks.damageEntity(j, amount, this.damageType[i], this.owner[i]);
      else w.hp[j] -= amount;
      if ((w.bodyFlags[j] & BodyFlag.NO_GRAVITY) === 0) {
        w.vx[j] += (this.vx[i] / sp) * 40;
        w.vy[j] += (this.vy[i] / sp) * 12;
      }
      return;
    }

    scratchHit.attacker = this.owner[i];
    scratchHit.target = j;
    scratchHit.weapon = wp;
    scratchHit.node = this.node[i];
    scratchHit.hand = this.hand[i];
    scratchHit.hx = sx;
    scratchHit.hy = sy;
    scratchHit.dirX = this.vx[i] / sp;
    scratchHit.dirY = this.vy[i] / sp;
    scratchHit.stats = this.stats[i] ?? defaultStats();
    scratchHit.rhythm = this.rhythm[i];
    scratchHit.charge = this.charge[i];
    scratchHit.backstab = false;
    scratchHit.windows = this.windows[i];
    scratchHit.chargePrimer = this.chargePrimer[i] as PrimerKindId | 0;

    const res = resolveHit(w, cells, scratchHit, scratchResult, rng);
    if (!res.hit) return;
    hooks.onHitConfirm?.(this.owner[i], this.hand[i], j, res);
  }

  /**
   * Terrain contact. Returns true when the round is spent — a ricochet is not.
   * The weapon's terrain interaction fires here, which is how a launcher opens
   * a room and a beam melts a hole rather than politely stopping at the wall.
   */
  private impact(
    w: World,
    cells: CellWorld,
    i: number,
    cx: number,
    cy: number,
    nvx: number,
    nvy: number,
    rng: Rng,
    hooks: ProjectileHooks,
  ): boolean {
    void w;
    const wp = this.wp[i];
    if (wp) applyTerrain(cells, wp.terrain, cx, cy, rng);

    if (hooks.particles && this.trailMat[i] !== VOID) {
      hooks.particles.emit(cx, cy, this.trailMat[i], 3, 60, 1.2, -nvx, -nvy);
    }

    if (this.ricochet[i] > 0) {
      this.ricochet[i]--;
      // Reflect off whichever axis actually blocked, so a shard skipping along
      // a floor keeps its forward run instead of bouncing straight back.
      const blockedX = cells.isSolid(cx + Math.sign(nvx), cy);
      const blockedY = cells.isSolid(cx, cy + Math.sign(nvy));
      this.vx[i] = blockedX || !blockedY ? -nvx * 0.7 : nvx * 0.7;
      this.vy[i] = blockedY || !blockedX ? -nvy * 0.7 : nvy * 0.7;
      this.x[i] -= Math.sign(nvx);
      this.y[i] -= Math.sign(nvy);
      return false;
    }

    this.spawnSplit(i, rng);
    return true;
  }

  private expire(cells: CellWorld, i: number, rng: Rng, hooks: ProjectileHooks): void {
    const wp = this.wp[i];
    if (wp) applyTerrain(cells, wp.terrain, this.x[i], this.y[i], rng);
    if (hooks.particles && this.trailMat[i] !== VOID) {
      hooks.particles.emit(this.x[i], this.y[i], this.trailMat[i], 2, 40, 1.6);
    }
    this.spawnSplit(i, rng);
  }

  private spawnSplit(i: number, rng: Rng): void {
    const n = this.split[i];
    if (n <= 0) return;
    const sp = Math.hypot(this.vx[i], this.vy[i]) || 1;
    for (let k = 0; k < n; k++) {
      const a = rng.range(0, Math.PI * 2);
      this.spawn({
        x: this.x[i], y: this.y[i],
        dirX: Math.cos(a), dirY: Math.sin(a),
        speed: sp * 0.6,
        lifetime: 0.4,
        pierce: 0,
        gravityScale: this.grav[i],
        radius: this.radius[i],
        owner: this.owner[i],
        team: this.team[i],
        damageType: this.damageType[i] as DamageTypeId,
        damage: this.damage[i] * 0.5,
        trailMat: VOID,
        homing: 0,
        ricochet: 0,
        splitOnImpact: 0,
        weapon: this.wp[i],
        node: this.node[i],
        stats: this.stats[i],
        rhythm: this.rhythm[i],
        charge: this.charge[i] * 0.5,
        chargePrimer: 0,
        hand: this.hand[i],
        windows: this.windows[i],
        // A fragment of a shell is still made of shell. Inheriting the parent's
        // outline is the only answer that does not need a second table.
        profile: this.profile[i],
      });
    }
  }

  private remembers(i: number, j: number): boolean {
    const base = i * HIT_MEMORY;
    for (let k = 0; k < this.hitN[i]; k++) if (this.hitBuf[base + k] === j) return true;
    return false;
  }

  private remember(i: number, j: number): void {
    const base = i * HIT_MEMORY;
    // Ring, not a grow: a pierce-99 rail round would otherwise need unbounded
    // memory, and forgetting the fourth-oldest body it already passed through
    // costs nothing because it is no longer near it.
    this.hitBuf[base + (this.hitN[i] % HIT_MEMORY)] = j;
    if (this.hitN[i] < HIT_MEMORY) this.hitN[i]++;
    else this.hitBuf[base] = j;
  }

  private remove(i: number): void {
    const last = --this.count;
    if (i !== last) {
      this.x[i] = this.x[last]; this.y[i] = this.y[last];
      this.vx[i] = this.vx[last]; this.vy[i] = this.vy[last];
      this.life[i] = this.life[last]; this.grav[i] = this.grav[last];
      this.radius[i] = this.radius[last]; this.pierce[i] = this.pierce[last];
      this.ricochet[i] = this.ricochet[last]; this.split[i] = this.split[last];
      this.owner[i] = this.owner[last]; this.team[i] = this.team[last];
      this.damage[i] = this.damage[last]; this.damageType[i] = this.damageType[last];
      this.trailMat[i] = this.trailMat[last]; this.homing[i] = this.homing[last];
      this.charge[i] = this.charge[last]; this.rhythm[i] = this.rhythm[last];
      this.windows[i] = this.windows[last]; this.chargePrimer[i] = this.chargePrimer[last];
      this.profile[i] = this.profile[last];
      this.wp[i] = this.wp[last]; this.node[i] = this.node[last];
      this.stats[i] = this.stats[last]; this.hand[i] = this.hand[last];
      this.hitN[i] = this.hitN[last];
      const a = i * HIT_MEMORY;
      const b = last * HIT_MEMORY;
      for (let k = 0; k < HIT_MEMORY; k++) this.hitBuf[a + k] = this.hitBuf[b + k];
    }
    this.wp[last] = null;
    this.node[last] = null;
    this.stats[last] = null;
  }

  clear(): void {
    for (let i = 0; i < this.count; i++) {
      this.wp[i] = null;
      this.node[i] = null;
      this.stats[i] = null;
    }
    this.count = 0;
  }
}

// ------------------------------------------------------------- fire handler

/**
 * The contact radius of a round fired from a node, in units.
 *
 * **`radius` does not mean the same thing on every hitbox kind, and this used to
 * read it as though it did.** `HitboxShape.radius` is documented as "half-width
 * (box), radius (circle/arc/ring/cone), or **length** (line)", and for a cone it
 * is the *range*. The line this replaces was `Math.max(2, hitbox.radius * 0.5)`,
 * which is right for the ten classes whose base hitbox is `spawn` (4–12 units,
 * a genuine round size) and catastrophic for the one that is not: the
 * Alchemical Sprayer's base cone has `radius: 90`, so every droplet it emitted
 * was a **radius-45 contact ball** — a 90-unit sphere on a 22-unit character,
 * four body heights across, four of them per shot at 14 shots a second, drawn as
 * a circle the width of the screen. Its heavy widens the cone to 120 and made
 * that 60.
 *
 * `thickness` is the field that means a cross-section on the non-`spawn` kinds —
 * "half-height (box), inner radius (ring), or **beam width** (line)" — and the
 * sprayer authors `thickness: 6`, which lands its droplets at 3 alongside the
 * seedthrower's 2.5 and the bandolier's 3. The cone that does the sprayer's
 * damage is untouched; what changes is that the *matter* it throws stops being a
 * second, enormous, undocumented hitbox.
 */
export function roundRadius(hb: { kind: string; radius: number; thickness: number }): number {
  return Math.max(2, (hb.kind === 'spawn' ? hb.radius : hb.thickness) * 0.5);
}

/**
 * The `WielderCtx.onFire` implementation. Reads the node's `FireSpec`, folds in
 * the resolved weapon's projectile modifiers, and puts `count` rounds in the
 * pool. This is the function whose absence made ten weapon classes inert.
 */
export function fireFromWeapon(
  proj: Projectiles,
  w: World,
  attacker: number,
  wp: ResolvedWeapon,
  node: ComboNode,
  hand: Hand,
  ox: number,
  oy: number,
  dirX: number,
  dirY: number,
  stats: CombatStats,
  rhythm: number,
  charge: number,
  chargePrimer: PrimerKindId | 0,
  windows: TriggerMask,
  rng: Rng,
): number {
  const f = node.fire;
  if (!f) return 0;
  const p = wp.projectile;
  const count = Math.max(1, f.count + p.countAdd);
  const spread = f.spread * p.spreadMul;
  const speed = f.speed * p.speedMul;
  const base = Math.atan2(dirY, dirX);
  const profile = roundProfileId(roundProfileForClass(String(wp.def.class)));
  let fired = 0;

  for (let k = 0; k < count; k++) {
    // A single round fires down the centre line; a cone spreads evenly and is
    // jittered, so a scattergun pattern is a pattern rather than a comb.
    const t = count === 1 ? 0 : (k / (count - 1)) * 2 - 1;
    const a = base + t * spread + rng.range(-spread * 0.15, spread * 0.15);
    const ok = proj.spawn({
      x: ox,
      y: oy,
      dirX: Math.cos(a),
      dirY: Math.sin(a),
      speed,
      lifetime: f.lifetime * p.lifetimeMul,
      pierce: f.pierce + p.pierceAdd,
      gravityScale: f.gravityScale * p.gravityScaleMul,
      radius: roundRadius(node.hitbox),
      owner: attacker,
      team: w.team[attacker],
      damageType: wp.damageType,
      damage: 0,
      trailMat: f.trailMat ?? VOID,
      homing: p.homing,
      ricochet: p.ricochet,
      splitOnImpact: p.splitOnImpact,
      weapon: wp,
      node,
      stats,
      rhythm,
      charge,
      chargePrimer,
      hand,
      windows,
      profile,
    });
    if (ok) fired++;
  }
  return fired;
}

/**
 * The whole `WielderCtx.onFire` wiring in one call. This is the function the
 * game shell installs; everything it needs is on the event.
 */
export function makeFireHandler(
  proj: Projectiles,
  w: World,
  rng: Rng,
): (e: FireEvent) => void {
  return (e) => {
    fireFromWeapon(
      proj, w, e.attacker, e.weapon, e.node, e.hand,
      e.x, e.y, e.dirX, e.dirY,
      e.state.stats, e.state.rhythm, e.charge, e.chargePrimer, e.windows, rng,
    );
  };
}

/** Flat-damage spawn for enemy `AttackDef`s — no weapon, no modules. */
export function fireFlat(
  proj: Projectiles,
  w: World,
  attacker: number,
  x: number,
  y: number,
  dirX: number,
  dirY: number,
  speed: number,
  damage: number,
  damageType: DamageTypeId,
  lifetime: number,
  gravityScale: number,
  radius: number,
): boolean {
  return proj.spawn({
    x, y, dirX, dirY, speed,
    lifetime,
    pierce: 0,
    gravityScale,
    radius,
    owner: attacker,
    team: w.team[attacker],
    damageType: damageType ?? DamageType.PHYSICAL,
    damage,
    trailMat: VOID,
    homing: 0,
    ricochet: 0,
    splitOnImpact: 0,
    weapon: null,
    node: null,
    stats: null,
    rhythm: 0,
    charge: 1,
    chargePrimer: 0,
    hand: 'main',
    windows: 0,
    // An enemy attack has no weapon class to look up, so it gets the drawing
    // every round used to have. This is the only caller that reaches `round`,
    // and `tests/projectileSilhouette.test.ts` §2 asserts it stays that way —
    // a *player* class resolving to the fallback would be one #85 missed.
    profile: 0,
  });
}
